import type { FantasyContest, FantasyContestPlayer, SalaryPoolEntry } from '@brush/api-client';

/**
 * Everything the contest screens know that isn't a component — §6's lifecycle as data, and every
 * sentence they say.
 *
 * <b>One file, for the reason `points/wording.ts` and `salaryWording.ts` are one file each.</b>
 * The lifecycle is four states, five transitions and a handful of refusals, and each of them is
 * described in more than one place on the screen: a status pill, an action button, a confirmation
 * that has to state a consequence, and a read-only notice explaining why a control has gone. Those
 * four have to agree, and the way to make them agree is to write them once.
 *
 * <b>It is deliberately not in `packages/ui`.</b> The salary model builder lives there because
 * §9.1 asks for it to be the points designer's screen with a different noun, and two consoles
 * render that. A contest has exactly one console — admin-web has no contest screen and is not
 * getting one — so this follows `LeaderboardPointsSection` and `LeaderboardScopeFilters` rather
 * than `SalaryModelBuilder`. The moment a second reader appears, the pure half moves and the
 * components stay, which is the split `LeaderboardBreakdown` already records.
 */

// ---------------------------------------------------------------- §6's four states

export type ContestStatus = 'draft' | 'open' | 'locked' | 'closed';

export const CONTEST_STATUSES: readonly ContestStatus[] = ['draft', 'open', 'locked', 'closed'];

/**
 * A contest's state in the room's own words, never the wire value.
 *
 * "Open" alone is ambiguous on a screen that also has a leaderboard called Running — open to
 * *what*? So each of these names the thing that is happening rather than the column's value.
 */
export const CONTEST_STATUS_LABEL: Record<ContestStatus, string> = {
  draft: 'Not published',
  open: 'Taking entries',
  locked: 'Locked — scoring',
  closed: 'Finished',
};

/** What is true right now, and what happens next. One sentence, shown under the status. */
export const CONTEST_STATUS_DETAIL: Record<ContestStatus, string> = {
  draft:
    'Nobody can see or enter this yet. Everything about it is still editable — the budget, the roster size, and every price in the pool.',
  open:
    'Entrants are building rosters against the prices you froze. The rules and the pool cannot change from here.',
  locked:
    'Entry has closed and rosters are fixed. Standings update each time the leaderboard behind it recalculates.',
  closed:
    'The final standings are written and nothing moves again. This is what entrants were paid against.',
};

/**
 * §6.2's Hold, in the room's words.
 *
 * <b>Two sentences and no event list.</b> The contest detail carries a boolean and a moment and
 * deliberately nothing else — `GET /{id}/drift` keeps the detail and is admin-only, because which
 * events changed is operator business. So this says what is true and where the answer lives rather
 * than guessing at the cause.
 *
 * §6.2 is explicit that this has to be surfaced: a held contest stops updating <em>entirely</em>,
 * including for events that were always in scope, and an unexplained stale board is worse than the
 * drift it is protecting against.
 */
export function pausedNotice(since: string | null): string {
  const when = since === null ? null : formatInstant(since);

  return (
    'Scoring is on hold. This contest’s event list changed after it locked, so its standings are ' +
    'held where they were rather than being quietly rescored against a series entrants did not ' +
    'draft against' +
    (when === null ? '.' : `, last updated ${when}.`)
  );
}

/**
 * §6.2's amended-after-lock marker, in the room's words.
 *
 * <b>Stated wherever the standings are, not only on the contest header.</b> An operator asked to
 * defend a set of standings is being asked about a board that changed after entrants committed to
 * it, and the fact belongs on the thing it is a fact about — the same argument that put
 * `standingsPaused` on the contest rather than leaving it to the admin-only drift route.
 *
 * <b>It is not a warning, and it must not read as one.</b> Adopting is a decision somebody made
 * deliberately, with a reason recorded on the snapshot; the honest case and a mistake produce
 * identical rows, so colouring it red would be wrong about half of them. Same call as the contact
 * change note on the memberships panel.
 */
export const AMENDED_AFTER_LOCK_NOTICE =
  'An administrator moved these standings after the contest locked — either by adopting a changed ' +
  'event list or by overriding the close. The reason is on the snapshot that did it, and every ' +
  'snapshot before it is still there.';

/**
 * §6.2's two outcomes, as the room reads them.
 *
 * <b>Hold has no control, and the wording has to say why rather than leaving a gap.</b> Hold is
 * what is already happening — the projection skips a drifted contest — so a button confirming it
 * would change nothing while reading as though something had been settled. What an administrator
 * decides is whether to leave it, which is not an action.
 */
export const DRIFT_HOLD_EXPLANATION =
  'Nothing is scoring while this stands. That is the safe outcome and it needs no decision — the ' +
  'standings stay exactly where entrants last saw them. Leaving it held is what happens if you ' +
  'close this screen.';

export const DRIFT_ADOPT_EXPLANATION =
  'Adopting takes the leaderboard’s event list as this contest’s and rescores every roster under ' +
  'it. Entrants drafted against the list frozen at lock, so this changes what they were competing ' +
  'for — the reason is recorded on the snapshot, the standings they were shown are kept, and the ' +
  'contest says it was amended after lock from then on.';

/**
 * How a drift delta reads on a screen: signed, and with the sign meaning what a reader expects.
 *
 * A points delta is signed the ordinary way. A <em>rank</em> delta is already positive-is-up on the
 * wire — the server subtracts the projected rank from the current one precisely so this does not
 * have to know — so both render with the same helper and neither flips a sign locally. A sign
 * flipped in one of two places is the failure mode this exists to avoid.
 */
export function signed(value: number, digits = 0): string {
  const text = Math.abs(value).toFixed(digits);
  if (value > 0) return `+${text}`;
  if (value < 0) return `\u2212${text}`;
  return digits > 0 ? text : '0';
}

/** Pill tone. Signal orange means "you must act" in this app, so only Draft earns `warn`. */
export const CONTEST_STATUS_TONE: Record<ContestStatus, 'neutral' | 'good' | 'warn'> = {
  draft: 'warn',
  open: 'good',
  locked: 'neutral',
  closed: 'neutral',
};

export function contestStatusOf(contest: { status: string }): ContestStatus {
  return CONTEST_STATUSES.includes(contest.status as ContestStatus)
    ? (contest.status as ContestStatus)
    : 'draft';
}

/**
 * §8.2: everything about a contest is editable while it is a Draft and nothing is afterwards.
 *
 * Asked as a question about the contest rather than spelled `status === 'draft'` at each call
 * site, because the reason is not the string — it is that every field on the record is something
 * an entrant drafted against.
 */
export function isEditable(contest: { status: string }): boolean {
  return contestStatusOf(contest) === 'draft';
}

/**
 * §5's discriminator.
 *
 * <b>Auction and Snake are shown disabled rather than omitted</b>, the same call `SegmentedControl`
 * already makes about the points designer's Expression option: a format that is coming is a fact
 * about the product, and a control listing only one choice says "this is all there is". §12 defers
 * both — auction needs real-time bidding and is the eventual source of honest salaries (§4.4).
 */
export type RosterFormat = 'salary_cap' | 'auction' | 'snake';

export const ROSTER_FORMATS: readonly {
  value: RosterFormat;
  label: string;
  disabled?: boolean;
  title?: string;
}[] = [
  { value: 'salary_cap', label: 'Salary cap' },
  {
    value: 'auction',
    label: 'Auction',
    disabled: true,
    title: 'Reserved for a later release — an auction needs live bidding between entrants.',
  },
  {
    value: 'snake',
    label: 'Snake draft',
    disabled: true,
    title: 'Reserved for a later release.',
  },
];

/**
 * Whether this format prices its players up front.
 *
 * <b>The one place the "a contest has a salary model" assumption is allowed to live.</b> A salary
 * cap contest is priced before anybody drafts; an auction discovers its prices by bidding and will
 * arrive with no model and no pool to review. So the salary-model and pool-review sections ask this
 * rather than testing the status or assuming a model exists, and `SalaryModelVersionId` stays
 * nullable everywhere it is read — which it also is for a salary cap contest priced by hand (§4.5's
 * manual pool).
 */
export function formatNeedsSalaries(format: string): boolean {
  return format === 'salary_cap';
}

export function rosterFormatLabel(format: string): string {
  return ROSTER_FORMATS.find((f) => f.value === format)?.label ?? format;
}

// ---------------------------------------------------------------- reading one

/** A lock time as a person reads it, with the zone named because the whole point is when. */
export function formatInstant(iso: string | null): string {
  if (!iso) return '—';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '—';
  return at.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

/**
 * A budget's unit, wherever a price is printed.
 *
 * §5 makes `BudgetUnitLabel` nullable and offers `"units"` or `"$"`. Salaries are budget units
 * rather than money by default, which is worth being consistent about: an operator who reads "200"
 * as dollars will price the pool as if it were a bankroll.
 */
export function budgetUnit(contest: Pick<FantasyContest, 'budgetUnitLabel'>): string {
  const label = contest.budgetUnitLabel?.trim();
  return label && label.length > 0 ? label : 'units';
}

/** "200 units", or "$200" when the room chose currency. Never a bare number in prose. */
export function withUnit(value: number, unit: string): string {
  const figure = Number(value.toFixed(2)).toLocaleString('en-US');
  return unit === '$' ? `$${figure}` : `${figure} ${unit}`;
}

// ---------------------------------------------------------------- transitions

export interface ContestTransition {
  /** The route this drives. */
  action: 'publish' | 'lock' | 'close';
  /** The button. */
  label: string;
  /** The dialog's heading. */
  title: string;
  /** What it does, and what it costs. Every claim here is irreversible. */
  claims: readonly string[];
  /** The button inside the dialog. */
  confirm: string;
}

/**
 * §6's transitions, one per state, and the claims each confirmation has to make.
 *
 * <b>Every one of these is one-way</b>, which is why each carries a list rather than a sentence:
 * the operator is about to do something with no undo, and the useful thing at that moment is what
 * stops being possible. Publish's first claim is the one this phase exists to state — the pool and
 * its prices freeze, and no route anywhere writes them again.
 */
export const CONTEST_TRANSITIONS: Partial<Record<ContestStatus, ContestTransition>> = {
  draft: {
    action: 'publish',
    label: 'Publish',
    title: 'Publish this contest?',
    claims: [
      'The pool and every price in it freeze. There is no route that edits them afterwards — not this screen, not the API, not a platform administrator. An entrant asking why somebody cost what they cost gets exactly what you publish here.',
      'The rules freeze with it: the budget, the roster size, the captain multiplier and which leaderboard it scores from. All of them are what somebody agreed to by entering.',
      'Entry opens straight away, and closes at the lock time in section 5.',
      'Editing the salary model afterwards changes nothing here. This contest keeps the exact version it publishes with.',
    ],
    confirm: 'Publish and open entry',
  },
  open: {
    action: 'lock',
    label: 'Lock entries now',
    title: 'Lock entries now?',
    claims: [
      'Entry closes immediately. Nobody can create, edit or withdraw a roster after this.',
      'It locks early. Entrants were told entry runs until the lock time in section 5, so do this only if the series has been brought forward or you have told them.',
      'The leaderboard’s event list is copied and frozen, and becomes what this contest is scored against. If somebody changes that list afterwards, standings pause rather than quietly rescoring.',
      'Scoring starts. Standings update each time the leaderboard behind it recalculates.',
    ],
    confirm: 'Lock entries',
  },
  locked: {
    action: 'close',
    label: 'Close contest',
    title: 'Close this contest?',
    claims: [
      'Final standings are written from the leaderboard’s latest figures, and marked final.',
      'Nothing moves again. Later leaderboard recalculations no longer reach this contest.',
      'Do this once the series is over and its leaderboard is settled — a board that recalculates after this needs a platform administrator to override, with a reason, on a contest people may already have been paid on.',
    ],
    confirm: 'Close and make final',
  },
};

/**
 * Why the transition on offer cannot be taken yet, or null when it can.
 *
 * <b>A reason, not a disabled button.</b> Publish is the control an operator is looking for from
 * the moment they open a draft, and a greyed one with no explanation reads as a broken screen. The
 * server refuses all of these too and refuses them properly — this only saves the round trip and
 * says which section to go and fix.
 */
export function publishBlockers(input: {
  poolSize: number;
  rosterSize: number;
  needsSalaries: boolean;
  poolDirty: boolean;
  contestDirty: boolean;
  eventsInScope: number | null;
  /**
   * How many scored events have <b>certainly</b> already run — `certainlyStartedEvents`, never
   * `startedEvents`. Null is "not resolved yet" and reads as silence, exactly as `eventsInScope`
   * does.
   */
  startedEventCount: number | null;
  /**
   * Which section names those events, so the reason can point at it. Passed rather than assumed
   * because the numbering shifts by one on a format that prices nobody, and the screen is what
   * decides it.
   */
  lockSection: number;
}): string[] {
  const blockers: string[] = [];

  if (input.contestDirty) {
    blockers.push('Save your changes first — publishing freezes the contest as stored, not as shown.');
  }

  if (input.poolDirty) {
    blockers.push(
      'The pool has unsaved changes. Save it first, or publishing would freeze the prices the server holds rather than the ones on screen.',
    );
  }

  if (input.needsSalaries && input.poolSize === 0) {
    blockers.push('There is nobody in the pool. Derive one in section 4, or add players by hand.');
  } else if (input.needsSalaries && input.poolSize < input.rosterSize) {
    blockers.push(
      `A roster is ${input.rosterSize} players and the pool holds ${input.poolSize}. Nobody could build a legal team, so the contest would take no entries at all.`,
    );
  }

  // Null is "not counted yet" and must not read as zero — the count arrives a moment after the
  // screen does, and a blocker that appears and then vanishes is worse than one that waits.
  if (input.eventsInScope === 0) {
    blockers.push(
      'The leaderboard in section 1 scores no events, so this contest would score nothing. Check its dates and filters.',
    );
  }

  // §6.1, as a reason rather than as a round trip. The server refuses this properly and names the
  // events; until it did so alone, *Publish and open entry* stayed live, the refusal went nowhere
  // an operator was looking, and the whole thing read as a dead click — Finding 5 of
  // `13-fantasy-walk.md`, and the mirror image of the unsaved-changes blockers a few lines above,
  // which had been doing this correctly all along.
  //
  // <b>The count is the strict one and must stay strict.</b> A blocker takes the button away, so it
  // may only fire where the client can be certain the server will refuse — see
  // `certainlyStartedEvents`, which is a different comparison from the warning's on purpose.
  //
  // <b>It names no events, deliberately.</b> The lock section lists every one of them with its id,
  // name and date, three sections up and still on screen; reprinting ten of them here would bury
  // the four other reasons a publish can be blocked under a list the operator has already read.
  if (input.startedEventCount !== null && input.startedEventCount > 0) {
    blockers.push(
      input.startedEventCount === 1
        ? `One event this contest scores has already run — section ${input.lockSection} names it. Entrants would be drafting with that result in front of them, so narrow the leaderboard’s scope in section 1, or point this contest at a series that is still to come.`
        : `${input.startedEventCount} events this contest scores have already run — section ${input.lockSection} names them. Entrants would be drafting with those results in front of them, so narrow the leaderboard’s scope in section 1, or point this contest at a series that is still to come.`,
    );
  }

  return blockers;
}

// ---------------------------------------------------------------- §6.1's lock timing

/**
 * The events a contest would score that <b>may</b> already have gone — §6.1's warning.
 *
 * <b>The client cannot answer this exactly and must not pretend to.</b> A tournament carries a
 * date and a wall-clock time with no offset modelled anywhere, and the server reads that pair as
 * UTC (`FantasyContestService.StartOf`) — a documented limitation rather than a bug, since no
 * honest conversion exists until a room carries a timezone. The scope-events endpoint reports only
 * the date, so this compares dates and says so wherever it is shown. The server is the control:
 * publish refuses with the events named, and that refusal is rendered whole.
 *
 * <b>`<=`, and the strict sibling below is not a duplicate to be folded into it.</b> An event
 * dated today may well have started, and this feeds a *warning* — being wrong here costs the
 * operator a glance at section 1, which is a good trade for catching the case early. Being wrong
 * the same way in a *blocker* costs them the publish, which is why `certainlyStartedEvents` exists
 * and compares differently.
 */
export function startedEvents<T extends { date: string }>(
  events: readonly T[],
  /** The <em>UTC</em> date, `yyyy-mm-dd`. See `certainlyStartedEvents` for why the frame matters. */
  todayUtc: string,
): T[] {
  return events.filter((e) => e.date <= todayUtc);
}

/**
 * The events a contest would score that have <b>certainly</b> gone — §6.1's publish blocker.
 *
 * <b>Two comparisons on one rule, and collapsing them breaks one of the two callers.</b> The
 * warning above is right to be eager. A blocker is not: it removes the control, so it may only
 * fire where the client can be sure the server will refuse. On `<=` it would refuse a perfectly
 * legal publish at 09:00 for an event starting at 20:00 tonight, with the server standing ready to
 * accept it and nothing on screen able to explain the disagreement.
 *
 * <b>`<` is exact rather than merely cautious, and the frame is what makes it so.</b> `todayUtc`
 * is the UTC date, and the server reads a tournament's date and wall-clock time as UTC too — the
 * timezone limitation, which here happens to work in our favour. So the latest instant an event
 * dated `d` can start, in the frame the server compares in, is `d` at 23:59Z; if `d` is strictly
 * before the current UTC date, that instant has gone whatever time of day the tournament carries.
 * Everything this returns has started by the server's own reckoning. Nothing it returns is a guess.
 *
 * Which is also why the date must be UTC rather than the browser's. A local date is a day ahead
 * for an operator west of Greenwich in the evening, and this would then block a publish the server
 * would have taken — the one direction a blocker may never be wrong in.
 *
 * The cost is the other direction, and it is deliberate: an event dated today that really has
 * started is missed here, so publishing is offered and the server refuses it. That refusal is
 * rendered whole inside the confirmation and scrolled to, which is the arrangement for a
 * disagreement the client has no way to settle.
 */
export function certainlyStartedEvents<T extends { date: string }>(
  events: readonly T[],
  todayUtc: string,
): T[] {
  return events.filter((e) => e.date < todayUtc);
}

/** The earliest event, which is what `locksAt` defaults to. */
export function firstEventDate(events: readonly { date: string }[]): string | null {
  let earliest: string | null = null;
  for (const event of events) {
    if (earliest === null || event.date < earliest) earliest = event.date;
  }
  return earliest;
}

/**
 * `yyyy-mm-ddThh:mm` for a datetime-local box, from an ISO instant the server sent.
 *
 * Rendered in the browser's own zone, which for a room's own operator is the room's zone. The
 * asymmetry with the server reading a tournament's time as UTC is stated on the screen rather than
 * papered over here — a converter that quietly picked one interpretation would make the two
 * disagree with nothing saying which was which.
 */
export function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}T${pad(at.getHours())}:${pad(at.getMinutes())}`;
}

/** The inverse, for sending. Empty means "you decide" — the server defaults it to §6.1's rule. */
export function fromLocalInput(value: string): string | null {
  if (value.trim() === '') return null;
  const at = new Date(value);
  return Number.isNaN(at.getTime()) ? null : at.toISOString();
}

/**
 * The lock time as the screen states it, from whatever is in the `datetime-local` box.
 *
 * <b>Guarded because a half-typed value is a real state.</b> Browsers differ on what a partially
 * filled datetime box reports — most give an empty string, some give a fragment — and
 * `new Date(fragment).toISOString()` throws a RangeError rather than returning something odd. That
 * would take down the section it is rendering, on a keystroke, which is not a trade worth making
 * for a summary line.
 *
 * Empty is not an error: it means §6.1's default, which is a better answer than anything this
 * screen could compute.
 */
export function describeLocksAt(value: string, whenEmpty: string): string {
  if (value.trim() === '') return whenEmpty;
  const at = new Date(value);
  return Number.isNaN(at.getTime()) ? whenEmpty : formatInstant(at.toISOString());
}

// ---------------------------------------------------------------- the pool draft

/**
 * One priced player as the review table holds it, which is the PUT's shape plus what the model
 * said about them.
 *
 * <b>Three of these fields are the derive's and not the contest's</b> — `metrics`,
 * `compositePercentile` and `droppedByModel`. They exist to explain a price while the operator is
 * looking at the run that produced it, and no column on `FantasyContestPlayer` holds them, so a row
 * read back from a saved pool has none of the three. The table says so rather than inventing a
 * figure, which is the same rule that keeps a client from computing a salary.
 *
 * <b>Which is why nothing may read provenance off them</b> — see `poolRowProvenance`, which is the
 * one place that decides where a price came from.
 */
export interface PoolRow {
  playerId: string;
  playerName: string;
  /**
   * `null` while the box is empty, which is a state rather than an accident.
   *
   * Zero is a legal price — an operator may deliberately make somebody free — so "not set yet"
   * needs its own spelling, and binding the input straight to a number would make clearing the box
   * mean "free". Same rule the points designer's number boxes follow, and the save is closed while
   * any row holds one.
   */
  salary: number | null;
  derivedSalary: number | null;
  isOverridden: boolean;
  overrideReason: string | null;
  isUnproven: boolean;
  displayRank: number | null;
  /** Present only for rows the current derive produced. */
  metrics: SalaryPoolEntry['metrics'] | null;
  compositePercentile: number | null;
  /**
   * In the saved pool but absent from the latest derive — the model no longer qualifies them.
   * Kept rather than dropped, because removing somebody an operator deliberately added would be
   * the derive silently discarding an adjustment.
   */
  droppedByModel: boolean;
}

/**
 * Where a row's price came from. Three states, not two.
 *
 * <b>Provenance is `derivedSalary`, and reading it off `metrics` was a defect.</b> Both readers
 * below — the row subtitle and the drill-down — used to ask whether `metrics` was null and call
 * everything without it hand-added. But `metrics` is the derive's working, which no column on a
 * contest holds, so `rowsFromPool` sets it to null on <em>every</em> row: a contest reopened a week
 * later reported all 150 model-priced players as somebody's manual decision. The data was right and
 * only the label lied — on the screen whose whole job is answering "why did this player cost that"
 * a year on, and it named a person who never existed. `metrics` answers "is the model's working in
 * front of me"; that is a different question and it is the caller's, since only the caller knows
 * whether it has figures to render instead of a label.
 *
 * `derivedSalary` is the fact that survives: the derive writes it, `poolRequestOf` sends it and
 * `rowsFromPool` reads it back, so it still says "the model produced a price for this player" after
 * a reload. That is the property provenance needs and the one `metrics` does not have.
 *
 * <b>And not `isOverridden` either</b>, which is the plausible wrong fix. That answers "did
 * somebody change this price", which is already said on the row twice over — the *Priced by hand*
 * badge and the reason box under it — and it is a different question: a model price an operator
 * then adjusted is <em>both</em>, and the two columns beside it exist precisely to show the model's
 * figure next to the operator's. Collapsing the two is what the misattribution was.
 *
 * <b>`droppedByModel` wins</b>, and it has to: `mergeDerived` keeps a leftover row's old
 * `derivedSalary`, so a player the model has just stopped qualifying still carries a figure from
 * the run before. It is session state and false on everything read back from the server — correct
 * rather than a second instance of the same bug, because "the model no longer qualifies them" is a
 * statement about a run that has just happened, and there has not been one.
 */
export type PoolRowProvenanceKind = 'derived' | 'handAdded' | 'droppedByModel';

export interface PoolRowProvenance {
  kind: PoolRowProvenanceKind;
  /** One clause, for the subtitle slot the model's figures would otherwise fill. */
  label: string;
  /** A sentence or two, for the drill-down that opened expecting those figures. */
  detail: string;
}

export function poolRowProvenance(row: PoolRow): PoolRowProvenance {
  if (row.droppedByModel) {
    return {
      kind: 'droppedByModel',
      label: 'No longer qualifies',
      detail:
        'The last run of the model did not qualify this player, so it has nothing to say about them. They are still in the pool — remove them if that was not deliberate.',
    };
  }

  if (row.derivedSalary === null) {
    return {
      kind: 'handAdded',
      label: 'Added by hand',
      detail:
        'Added by hand, so no model figures exist for them. Their price is whatever you set it to.',
    };
  }

  // The common state, and the one that had no wording at all. It says what is true and stops: the
  // model produced a price, the figures behind it are not kept, and nothing here claims a number
  // it does not have or a person who did not act.
  return {
    kind: 'derived',
    label: 'Priced by the model · figures not kept',
    detail:
      'The model produced this price. The figures behind it — cashes, points, earnings — belong to the run that worked it out and are not kept on the contest, so a pool read back afterwards has the price and not the reasoning.',
  };
}

export function rowsFromPool(pool: readonly FantasyContestPlayer[]): PoolRow[] {
  return pool.map((p) => ({
    playerId: p.playerId,
    playerName: p.playerName,
    salary: p.salary,
    derivedSalary: p.derivedSalary,
    isOverridden: p.isOverridden,
    overrideReason: p.overrideReason,
    isUnproven: p.isUnproven,
    displayRank: p.displayRank,
    metrics: null,
    compositePercentile: null,
    droppedByModel: false,
  }));
}

/**
 * Fold a fresh derive into whatever the operator already has.
 *
 * <b>An override survives a re-derive, and that is the whole reason `IsOverridden` is a column.</b>
 * The service note on `POST /pool/derive` says it in as many words — a derive that saved would make
 * every re-run discard the adjustments — and the same is true of a merge that overwrote them. So:
 *
 * - Every row takes the model's new `derivedSalary`, because that is what the model now says and
 *   an operator comparing their override against a stale figure is comparing against nothing.
 * - Only rows that are **not** overridden take the new price.
 * - A row the model no longer qualifies is kept and marked, not deleted.
 * - A row the operator added by hand has no derived salary and simply stays.
 */
export function mergeDerived(existing: readonly PoolRow[], derived: readonly SalaryPoolEntry[]): PoolRow[] {
  const byId = new Map(existing.map((row) => [row.playerId, row]));
  const merged: PoolRow[] = [];

  for (const entry of derived) {
    const prior = byId.get(entry.playerId);
    byId.delete(entry.playerId);

    merged.push({
      playerId: entry.playerId,
      playerName: entry.playerName,
      salary: prior?.isOverridden ? prior.salary : entry.salary,
      derivedSalary: entry.salary,
      isOverridden: prior?.isOverridden ?? false,
      overrideReason: prior?.isOverridden ? prior.overrideReason : null,
      isUnproven: entry.isUnproven,
      displayRank: entry.displayRank,
      metrics: entry.metrics,
      compositePercentile: entry.compositePercentile,
      droppedByModel: false,
    });
  }

  // Whatever the derive did not mention: hand-added players, and anybody who has stopped
  // qualifying. Both are the operator's, so both stay.
  for (const leftover of byId.values()) {
    merged.push({ ...leftover, metrics: null, compositePercentile: null, droppedByModel: true });
  }

  return sortPool(merged);
}

/**
 * Cheapest last, so the strongest players — the ones a roster is built around, and the ones an
 * operator is actually checking — are at the top.
 *
 * `displayRank` when the model produced one, price otherwise, then name so the order is total. A
 * hand-added row has neither rank nor a model behind it and sorts by its price like everything
 * else.
 */
export function sortPool(rows: readonly PoolRow[]): PoolRow[] {
  // An unpriced row sorts to the bottom rather than to the top: it is a row wanting attention, and
  // the operator finds it through the error naming them rather than by it displacing the pool's
  // most expensive players.
  return [...rows].sort(
    (a, b) =>
      (b.salary ?? -Infinity) - (a.salary ?? -Infinity) ||
      (a.displayRank ?? Number.MAX_SAFE_INTEGER) - (b.displayRank ?? Number.MAX_SAFE_INTEGER) ||
      a.playerName.localeCompare(b.playerName),
  );
}

/**
 * The rules `FantasyPoolValidator` enforces, answered while the operator types.
 *
 * Feedback, never the control — same relationship `points/validate.ts` has to the server's
 * validator. What it buys is that the reason box appears *with* the override rather than after a
 * failed save, which is the difference between a required field and a rejected request.
 */
export function poolProblems(rows: readonly PoolRow[]): string[] {
  const problems: string[] = [];
  const missingReason = rows.filter((r) => r.isOverridden && !r.overrideReason?.trim());
  const unpriced = rows.filter((r) => r.salary === null);
  const negative = rows.filter((r) => r.salary !== null && r.salary < 0);

  if (unpriced.length > 0) {
    problems.push(
      unpriced.length === 1
        ? `${unpriced[0]!.playerName} has no price. Zero is allowed and means free — an empty box is not the same thing.`
        : `${unpriced.length} players have no price. Zero is allowed and means free — an empty box is not the same thing.`,
    );
  }

  if (missingReason.length > 0) {
    problems.push(
      missingReason.length === 1
        ? `${missingReason[0]!.playerName} has a price you set by hand and no reason for it. An override with no reason is an unexplained price on a document entrants draft against.`
        : `${missingReason.length} players have a price you set by hand and no reason for it. An override with no reason is an unexplained price on a document entrants draft against.`,
    );
  }

  if (negative.length > 0) {
    problems.push(
      `${negative.length === 1 ? `${negative[0]!.playerName} has` : `${negative.length} players have`} a price below zero, which would pay an entrant budget for drafting somebody. Zero is allowed; below it is not.`,
    );
  }

  return problems;
}

/** A stable string for the pool, for the draft-versus-stored dirty check. */
export function poolKey(rows: readonly PoolRow[]): string {
  return JSON.stringify(
    sortPool(rows).map((r) => [
      r.playerId,
      r.salary,
      r.derivedSalary,
      r.isOverridden,
      r.overrideReason?.trim() || null,
      r.isUnproven,
      r.displayRank,
    ]),
  );
}

// ---------------------------------------------------------------- the contest draft

/**
 * The fields section 1 and section 2 edit — everything `PATCH /api/fantasy-contests/{id}` accepts
 * and nothing else.
 *
 * `entryFee` and `status` are deliberately absent rather than carried and ignored. §12 constrains
 * the fee to zero and there is no control for it anywhere, and `status` is ignored by the server in
 * favour of §6's four routes — a draft field for either would be a control somebody could wire up
 * later without noticing what it means.
 */
export interface ContestDraft {
  name: string;
  description: string;
  leaderboardId: string;
  /**
   * Which published salary model version prices the pool, or null for §4.5's hand-priced one.
   *
   * <b>A field of the contest now, not browser state.</b> It used to live in session storage
   * because the server had nowhere to put it until publish — so reopening the builder elsewhere,
   * or handing the draft to a colleague, lost the selection while the derived prices remained.
   * §6 opened the write window and the hack went with it.
   */
  salaryModelVersionId: string | null;
  rosterFormat: RosterFormat;
  rosterSize: number | null;
  budget: number | null;
  budgetUnitLabel: string;
  captainEnabled: boolean;
  captainMultiplier: number | null;
  maxEntriesPerPlayer: number | null;
}

export function contestDraftOf(contest: FantasyContest): ContestDraft {
  return {
    name: contest.name,
    description: contest.description ?? '',
    leaderboardId: contest.leaderboardId,
    salaryModelVersionId: contest.salaryModelVersionId,
    rosterFormat: (contest.rosterFormat as RosterFormat) ?? 'salary_cap',
    rosterSize: contest.rosterSize,
    budget: contest.budget,
    budgetUnitLabel: contest.budgetUnitLabel ?? '',
    captainEnabled: contest.captainEnabled,
    captainMultiplier: contest.captainMultiplier,
    maxEntriesPerPlayer: contest.maxEntriesPerPlayer,
  };
}

/** A stable string for the dirty check — draft against last stored, the template editor's rule. */
export function contestDraftKey(draft: ContestDraft): string {
  return JSON.stringify([
    draft.name.trim(),
    draft.description.trim(),
    draft.leaderboardId,
    draft.salaryModelVersionId,
    draft.rosterFormat,
    draft.rosterSize,
    draft.budget,
    draft.budgetUnitLabel.trim(),
    draft.captainEnabled,
    draft.captainEnabled ? draft.captainMultiplier : null,
    draft.maxEntriesPerPlayer,
  ]);
}

/**
 * The rules `FantasyContestValidator` enforces, answered while the operator types.
 *
 * Feedback, never the control. The captain pair is the one worth having client-side: §5.1 spends
 * half a page on the fact that captains-on with no multiplier is the state the CHECK constraint
 * exists to prevent, and it is reachable here by ticking a box and clearing a number box.
 *
 * <b>Tagged by section</b>, following `salaryIssuesFor`: a problem is shown under the control that
 * causes it, not collected at the foot of the form where the operator has to work out which of six
 * sections it belongs to. Section 6 restates all of them together, because that is the one place
 * the whole contest is read at once.
 */
export type ContestIssueSection = 'source' | 'format';

export interface ContestIssue {
  section: ContestIssueSection;
  message: string;
}

export function contestProblems(draft: ContestDraft): ContestIssue[] {
  const problems: ContestIssue[] = [];
  const fail = (section: ContestIssueSection, message: string) =>
    problems.push({ section, message });

  if (draft.name.trim().length === 0) fail('source', 'A contest needs a name — entrants see it.');
  if (draft.leaderboardId === '') {
    fail('source', 'Pick the leaderboard this contest scores from.');
  }

  if (draft.rosterSize === null || draft.rosterSize < 1) {
    fail('format', 'A roster has to be at least one player.');
  }

  if (draft.budget === null || draft.budget <= 0) {
    fail(
      'format',
      'The budget has to be more than zero, or every roster is free and nothing binds.',
    );
  }

  if (draft.captainEnabled && (draft.captainMultiplier === null || draft.captainMultiplier <= 0)) {
    fail(
      'format',
      'A captain needs a multiplier above zero. With captains on and no multiplier, the one player an entrant picked out would score nothing.',
    );
  }

  if (draft.maxEntriesPerPlayer !== null && draft.maxEntriesPerPlayer < 1) {
    fail('format', 'Somebody has to be able to enter at least once.');
  }

  return problems;
}

/** One section's issues, in the shape `IssueList` renders. */
export function contestIssuesFor(
  issues: readonly ContestIssue[],
  section: ContestIssueSection,
): { severity: 'error'; message: string }[] {
  return issues
    .filter((issue) => issue.section === section)
    .map((issue) => ({ severity: 'error' as const, message: issue.message }));
}

/**
 * Preserve what the model said about a player across a refetch of the stored pool.
 *
 * The metrics behind a price are the derive's and are **not** stored on a contest — so a pool read
 * back from the server has none, and re-seeding the table from it after a save would collapse every
 * drill-down the operator had open and leave the strength column blank on rows they had just been
 * reading. Carrying them across is presentation only: nothing here changes a price, a flag or a
 * reason, all of which come from the server's copy.
 */
export function carryModelFacts(stored: readonly PoolRow[], previous: readonly PoolRow[]): PoolRow[] {
  const before = new Map(previous.map((row) => [row.playerId, row]));

  return stored.map((row) => {
    const prior = before.get(row.playerId);
    return prior
      ? {
          ...row,
          metrics: prior.metrics,
          compositePercentile: prior.compositePercentile,
          droppedByModel: prior.droppedByModel,
        }
      : row;
  });
}

/** The PUT's shape, from the table's rows. Trims a reason so a box of spaces is not a reason. */
export function poolRequestOf(rows: readonly PoolRow[]) {
  return rows.map((row) => ({
    playerId: row.playerId,
    // Unreachable: `poolProblems` reports an unpriced row and the save is closed while it does.
    // Defaulted rather than asserted, because a crash here would lose the whole pool.
    salary: row.salary ?? 0,
    derivedSalary: row.derivedSalary,
    isOverridden: row.isOverridden,
    overrideReason: row.isOverridden ? (row.overrideReason?.trim() ?? null) : null,
    isUnproven: row.isUnproven,
    displayRank: row.displayRank,
  }));
}

/**
 * Whether the model about to be pinned is the model that actually produced these prices.
 *
 * <b>The one way this screen could write down something untrue.</b> The contest records which
 * version it is priced by, and the prices come from whatever ran in section 4 — and nothing joins
 * them, because `POST /pool/derive` stores per-row provenance and not which model it ran. So an
 * operator who derives with one model, changes their mind in section 3 and publishes has produced
 * a contest whose stated model did not price it, with no way for anybody afterwards to tell.
 *
 * <b>A warning rather than a blocker</b>, and deliberately: both states it catches are things an
 * operator may genuinely mean. Curating a derived pool down to a hand-priced one is §4.5's manual
 * path arrived at from the other direction, and re-pointing at a model you are about to re-run is
 * an ordinary intermediate step. What is not acceptable is doing either by accident, so it is said
 * where the choice is made and again at the confirmation.
 *
 * <b>Only `derivedWith` is browser state now.</b> The *chosen* model is a field of the contest
 * since §6 opened its write window, so the first case — derived prices with no model chosen —
 * survives a reload and is the one that would otherwise end in a wrong record. Which version
 * actually produced the rows is still nowhere on the server: `POST /pool/derive` records per-row
 * provenance and not which model it ran, so that half is known only while the screen is open.
 */
export function salaryModelMismatch(
  rows: readonly PoolRow[],
  chosen: string | null,
  derivedWith: string | null,
): string | null {
  const derived = rows.filter((row) => row.derivedSalary !== null).length;

  if (derived > 0 && chosen === null) {
    return `${derived === 1 ? 'One price below was' : `${derived} prices below were`} worked out by a salary model, but no model is chosen in section 3. Publishing now records this pool as priced by hand, and which model produced those figures is then lost for good. Pick it again, or accept that.`;
  }

  if (chosen !== null && derivedWith !== null && derivedWith !== chosen) {
    return 'The prices below came from a different salary model than the one now chosen in section 3. Re-run the model so the two agree — otherwise this contest will record a model that did not price it.';
  }

  return null;
}

/**
 * Who a roster member is, from the contest's own pool rather than the room's whole roster (performance
 * rank 2): every member is a pool player, since a lineup naming anybody else is refused. Null while the
 * pool has not arrived, so a roster is not drawn with placeholder names; a member somehow absent from it
 * reads as "A player at this room".
 */
export function memberNames(
  pool: { players: readonly Pick<FantasyContestPlayer, 'playerId' | 'playerName'>[] } | undefined,
): ((playerId: string) => string) | null {
  if (!pool) return null;
  const names = new Map(pool.players.map((p) => [p.playerId, p.playerName]));
  return (playerId) => names.get(playerId) ?? 'A player at this room';
}
